/**
 * urlState.js
 *
 * Serialize / parse filter + view state into URL search params so dashboard and
 * case-list views can be bookmarked and shared.
 */

import {
  DASHBOARD_DEFAULT_PRESET,
  defaultDashboardRange,
} from '../lib/caseLoadPolicy.js';
import { STATUS_KEYS as SHARED_STATUS_KEYS } from '@shared/statusRules.js';
import { EVENT_CATEGORY_KEYS } from '@shared/eventCategory.js';

// ── Helpers ────────────────────────────────────────────────────────────────────

export function paramsEqual(a, b) {
  const sa = a instanceof URLSearchParams ? a : new URLSearchParams(a);
  const sb = b instanceof URLSearchParams ? b : new URLSearchParams(b);
  if (sa.toString() === sb.toString()) return true;
  const keys = new Set([...sa.keys(), ...sb.keys()]);
  for (const key of keys) {
    if (sa.getAll(key).join('\0') !== sb.getAll(key).join('\0')) return false;
  }
  return true;
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function parseEnabledFlags(keys, raw, defaults) {
  if (!raw) return { ...defaults };
  const enabled = new Set(
    raw.split(',').map(s => s.trim()).filter(Boolean),
  );
  const out = { ...defaults };
  for (const key of keys) out[key] = enabled.has(key);
  return out;
}

function serializeEnabledFlags(flags, defaults) {
  const keys = Object.keys(defaults);
  const enabled = keys.filter(k => flags[k]);
  const defaultEnabled = keys.filter(k => defaults[k]);
  if (setsEqual(new Set(enabled), new Set(defaultEnabled))) return null;
  return enabled.join(',');
}

function isoDate(d) {
  if (!(d instanceof Date) || isNaN(d)) return '';
  return d.toISOString().slice(0, 10);
}

// ── Dashboard ────────────────────────────────────────────────────────────────

const DASHBOARD_SCOPE = new Set(['all', 'region', 'clinic']);
const DASHBOARD_RANGE_PRESETS = new Set(['30', '60', 'year', 'all', 'custom']);
const DASHBOARD_COUNTRY_CODE = {
  UK: 'United Kingdom',
  IE: 'Ireland',
  US: 'United States',
};
const DASHBOARD_COUNTRY_SHORT = Object.fromEntries(
  Object.entries(DASHBOARD_COUNTRY_CODE).map(([code, name]) => [name, code]),
);

export function parseDashboardParams(searchParams) {
  const sp = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams);

  let scope = sp.get('scope') || 'all';
  if (!DASHBOARD_SCOPE.has(scope)) scope = 'all';

  const region = sp.get('region') || '';
  const clinic = sp.get('clinic') || '';
  const countryCode = (sp.get('country') || '').trim().toUpperCase();
  const country = DASHBOARD_COUNTRY_CODE[countryCode] || '';

  let rangePreset = sp.get('range') || DASHBOARD_DEFAULT_PRESET;
  if (!DASHBOARD_RANGE_PRESETS.has(rangePreset)) rangePreset = DASHBOARD_DEFAULT_PRESET;

  const from = sp.get('from') || '';
  const to   = sp.get('to')   || '';

  // Brush-selected window without an explicit preset uses from/to only.
  if (!sp.get('range') && from && to) rangePreset = 'custom';

  return { scope, country, region, clinic, rangePreset, from, to };
}

/** Map parsed URL values onto dashboard range state ({ start, end } | null). */
export function dashboardRangeFromParams({ rangePreset, from, to }) {
  if (rangePreset === '30') {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const start = new Date(end.getTime() - 30 * 864e5); start.setHours(0, 0, 0, 0);
    return { range: { start, end }, activePreset: '30', customExpanded: false };
  }
  if (rangePreset === '60') {
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const start = new Date(end.getTime() - 60 * 864e5); start.setHours(0, 0, 0, 0);
    return { range: { start, end }, activePreset: '60', customExpanded: false };
  }
  if (rangePreset === 'year') {
    return { range: defaultDashboardRange(), activePreset: 'year', customExpanded: false };
  }
  if (rangePreset === 'custom' && from && to) {
    const start = new Date(`${from}T00:00:00`);
    const end   = new Date(`${to}T23:59:59`);
    if (!isNaN(start) && !isNaN(end) && end >= start) {
      return { range: { start, end }, activePreset: 'custom', customExpanded: false };
    }
  }
  if (rangePreset === 'custom') {
    // Custom preset selected but dates not committed to the URL yet — keep the
    // picker open and leave the current brush window untouched (range omitted).
    return { range: undefined, activePreset: 'custom', customExpanded: true };
  }
  if (rangePreset === 'all') {
    return { range: null, activePreset: 'all', customExpanded: false };
  }
  return { range: null, activePreset: 'all', customExpanded: false };
}

export function buildDashboardParams({
  scopeMode,
  scopeCountry,
  scopeRegion,
  scopeClinic,
  activePreset,
  range,
}) {
  const p = new URLSearchParams();

  if (scopeMode && scopeMode !== 'all') p.set('scope', scopeMode);
  if (scopeMode === 'region' && scopeCountry) {
    const code = DASHBOARD_COUNTRY_SHORT[scopeCountry];
    if (code) p.set('country', code);
  }
  if (scopeMode === 'region' && scopeRegion) p.set('region', scopeRegion);
  if (scopeMode === 'clinic' && scopeClinic) p.set('clinic', scopeClinic);

  if (activePreset === '30' || activePreset === '60' || activePreset === 'year') {
    p.set('range', activePreset);
  } else if (activePreset === 'all') {
    p.set('range', 'all');
  } else if (range?.start && range?.end) {
    p.set('range', 'custom');
    p.set('from', isoDate(range.start));
    p.set('to', isoDate(range.end));
  } else if (activePreset === 'custom') {
    p.set('range', 'custom');
  }

  return p;
}

// ── Case list ────────────────────────────────────────────────────────────────

// The `?status=` and `?treatment=` vocabularies. Imported, not restated: the
// by-clinic rollup that Thérapie Maps' clinic card reads emits these same keys and
// builds drill-down links out of them, so a third copy here could silently make a
// link land on a filter that means something else.
const STATUS_KEYS = SHARED_STATUS_KEYS;
const TREATMENT_KEYS = EVENT_CATEGORY_KEYS;
const DATE_PRESET_TO_LABEL = {
  '7d':  'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last quarter',
  '365d': 'Last year',
  custom: 'Custom range',
  all: 'All time',
};
const DATE_LABEL_TO_PRESET = Object.fromEntries(
  Object.entries(DATE_PRESET_TO_LABEL).map(([k, v]) => [v, k]),
);

export const CASE_LIST_DEFAULTS = {
  filterStatuses: { new: true, open: true, pending: true, claims: true, closed: false },
  filterTreatments: { lhr: true, awi: true, other: true, nearMiss: true, clinicIncident: true },
  filterSeverities: {
    'Code 1': false, 'Code 2': false, 'Code 3': false, 'Code 4': false, 'Code 5': false,
  },
  filterCountries: { UK: false, IE: false, US: false },
  filterClinics: new Set(),
  tagStates: {},        // slug → 'has' | 'not' (tri-state tag filter)
  searchText: '',
  dateRange: 'All time',
  customDateFrom: '',
  customDateTo: '',
  page: 1,
  sort: { col: 'reportedDate', dir: 'desc' },
  showDeleted: false,
  inactiveDays: 0,   // 0 = any; otherwise only cases untouched for ≥ N days
  viewAll: false,    // "All" view: every live case, sidebar filters set aside
};

/** Allowed values of the "Inactive for" filter (days). */
export const INACTIVE_DAY_OPTIONS = Object.freeze([7, 14, 30, 90]);

export function parseCaseListParams(searchParams, config = {}) {
  const sp = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(searchParams);

  const defaults = {
    ...CASE_LIST_DEFAULTS,
    filterSeverities: {
      ...Object.fromEntries((config.severityCodes || []).map(c => [c.value, false])),
      '(none)': false,   // "No code" chip — cases with no severity
    },
    sort: config.defaultSort || CASE_LIST_DEFAULTS.sort,
  };

  // Bookmarked URLs from before the Triage → Pending rename carry the old key.
  const rawStatus = sp.get('status');
  const filterStatuses = parseEnabledFlags(
    STATUS_KEYS, rawStatus && rawStatus.replace(/\btriage\b/g, 'pending'), defaults.filterStatuses,
  );
  const filterTreatments = parseEnabledFlags(
    TREATMENT_KEYS, sp.get('treatment'), defaults.filterTreatments,
  );

  const filterSeverities = { ...defaults.filterSeverities };
  const sevRaw = sp.get('severity');
  if (sevRaw) {
    for (const key of Object.keys(filterSeverities)) filterSeverities[key] = false;
    for (const code of sevRaw.split(',').map(s => s.trim()).filter(Boolean)) {
      if (code in filterSeverities) filterSeverities[code] = true;
    }
  }

  const filterCountries = { ...defaults.filterCountries };
  const countryRaw = sp.get('country');
  if (countryRaw) {
    filterCountries.UK = false;
    filterCountries.IE = false;
    filterCountries.US = false;
    for (const c of countryRaw.split(',').map(s => s.trim().toUpperCase())) {
      if (c === 'UK') filterCountries.UK = true;
      if (c === 'IE') filterCountries.IE = true;
      if (c === 'US') filterCountries.US = true;
    }
  }

  const filterClinics = new Set(sp.getAll('clinic').filter(Boolean));

  // Tag filter — by slug, repeatable (?tag=sp_migrated&tag=near_miss).
  // Tag filter — tri-state per tag: ?tag=<slug> = must have (any of), ?nottag=<slug>
  // = must not have. Legacy ?tagmode=exclude turns the ?tag list into "not".
  const tagStates = {};
  const legacyExclude = sp.get('tagmode') === 'exclude';
  for (const slug of sp.getAll('tag').filter(Boolean)) tagStates[slug] = legacyExclude ? 'not' : 'has';
  for (const slug of sp.getAll('nottag').filter(Boolean)) tagStates[slug] = 'not';

  const searchText = sp.get('q') || '';

  let dateRange = defaults.dateRange;
  let customDateFrom = '';
  let customDateTo = '';
  const datePreset = sp.get('date');
  if (datePreset) {
    if (datePreset === 'custom') {
      dateRange = 'Custom range';
      customDateFrom = sp.get('from') || '';
      customDateTo   = sp.get('to')   || '';
    } else {
      dateRange = DATE_PRESET_TO_LABEL[datePreset] || defaults.dateRange;
    }
  }

  const page = Math.max(1, parseInt(sp.get('page') || '1', 10) || 1);

  let sort = { ...defaults.sort };
  const sortRaw = sp.get('sort');
  if (sortRaw) {
    const [col, dir] = sortRaw.split(':');
    if (col && (dir === 'asc' || dir === 'desc')) sort = { col, dir };
  }

  const showDeleted = sp.get('deleted') === '1';

  const inactiveRaw = parseInt(sp.get('inactive') || '0', 10);
  const inactiveDays = INACTIVE_DAY_OPTIONS.includes(inactiveRaw) ? inactiveRaw : 0;

  const viewAll = sp.get('all') === '1';

  return {
    filterStatuses,
    filterTreatments,
    filterSeverities,
    filterCountries,
    filterClinics,
    tagStates,
    searchText,
    dateRange,
    customDateFrom,
    customDateTo,
    page,
    sort,
    showDeleted,
    inactiveDays,
    viewAll,
  };
}

export function buildCaseListParams({
  filterStatuses,
  filterTreatments,
  filterSeverities,
  filterCountries,
  filterClinics,
  tagStates = {},
  searchText,
  dateRange,
  customDateFrom,
  customDateTo,
  page,
  sort,
  showDeleted,
  inactiveDays = 0,
  viewAll = false,
}, config = {}) {
  const p = new URLSearchParams();
  const defaults = {
    ...CASE_LIST_DEFAULTS,
    sort: config.defaultSort || CASE_LIST_DEFAULTS.sort,
  };

  const status = serializeEnabledFlags(filterStatuses, defaults.filterStatuses);
  if (status) p.set('status', status);

  const treatment = serializeEnabledFlags(filterTreatments, defaults.filterTreatments);
  if (treatment) p.set('treatment', treatment);

  const sevEnabled = Object.entries(filterSeverities)
    .filter(([, on]) => on)
    .map(([k]) => k);
  if (sevEnabled.length) p.set('severity', sevEnabled.join(','));

  const countries = [];
  if (filterCountries.UK) countries.push('UK');
  if (filterCountries.IE) countries.push('IE');
  if (filterCountries.US) countries.push('US');
  if (countries.length) p.set('country', countries.join(','));

  for (const clinic of [...filterClinics].sort()) p.append('clinic', clinic);

  const tagSlugs = Object.keys(tagStates || {}).sort();
  for (const slug of tagSlugs) if (tagStates[slug] === 'has') p.append('tag', slug);
  for (const slug of tagSlugs) if (tagStates[slug] === 'not') p.append('nottag', slug);

  if (searchText?.trim()) p.set('q', searchText.trim());

  const datePreset = DATE_LABEL_TO_PRESET[dateRange];
  if (datePreset && datePreset !== 'all') {
    p.set('date', datePreset);
    if (datePreset === 'custom') {
      if (customDateFrom) p.set('from', customDateFrom);
      if (customDateTo)   p.set('to', customDateTo);
    }
  }

  if (page > 1) p.set('page', String(page));

  const defSort = defaults.sort;
  if (sort.col !== defSort.col || sort.dir !== defSort.dir) {
    p.set('sort', `${sort.col}:${sort.dir}`);
  }

  if (showDeleted) p.set('deleted', '1');

  if (INACTIVE_DAY_OPTIONS.includes(inactiveDays)) p.set('inactive', String(inactiveDays));

  if (viewAll && !showDeleted) p.set('all', '1');

  return p;
}

// ── Share link ───────────────────────────────────────────────────────────────

export async function copyShareableLink() {
  const url = window.location.href;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(url);
    return url;
  }
  const ta = document.createElement('textarea');
  ta.value = url;
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  document.body.appendChild(ta);
  ta.select();
  document.execCommand('copy');
  document.body.removeChild(ta);
  return url;
}
